// LandingFooter — T-011. Language switcher (DE / HU / EN), install-app
// hint, and links. The switcher calls setLocale() from the i18n module and
// writes the choice to localStorage so it survives a reload.

import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { persistLocale } from "../../i18n/detectLocale";
import type { Locale } from "../../types/i18n";
import { InstallButton } from "../pwa/InstallButton";

const LANG_ORDER: Locale[] = ["de", "hu", "en"];
const LANG_LABEL: Record<Locale, string> = { de: "DE", hu: "HU", en: "EN" };

export function LandingFooter(): JSX.Element {
  const { t, locale, setLocale } = useI18n();
  const active = locale;
  const onPick = (next: Locale) => {
    persistLocale(next);
    setLocale(next);
  };
  return (
    <footer
      aria-label="footer"
      className="border-t border-[var(--color-line)] bg-[var(--color-void)]"
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-6 py-12 sm:flex-row sm:items-start sm:justify-between sm:px-10">
        <div>
          <p className="font-display text-2xl uppercase tracking-[0.02em] text-[var(--color-lime)]">
            {t("brand.name")}
          </p>
          <p
            className="mt-2 max-w-sm font-sans text-sm text-[var(--color-ink-mute)]"
            style={{ lineHeight: 1.5 }}
          >
            {t("landing.footer.tagline")}
          </p>
        </div>
        <div className="flex flex-col gap-6 sm:flex-row sm:gap-12">
          <nav aria-label="language" className="flex flex-col gap-2">
            <p className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
              {t("landing.footer.language")}
            </p>
            <ul className="flex gap-2">
              {LANG_ORDER.map((l) => {
                const selected = active === l;
                return (
                  <li key={l}>
                    <button
                      type="button"
                      onClick={() => onPick(l)}
                      aria-pressed={selected}
                      className={
                        "px-3 py-2 font-subhead text-sm uppercase tracking-[0.06em] transition-colors duration-[120ms] " +
                        (selected
                          ? "bg-[var(--color-lime)] text-[var(--color-void)]"
                          : "border border-[var(--color-line-strong)] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]")
                      }
                      style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
                    >
                      {LANG_LABEL[l]}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <nav aria-label="legal" className="flex flex-col gap-2">
            <p className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
              {t("landing.footer.legal")}
            </p>
            <ul className="flex flex-col gap-1 font-sans text-sm">
              <li>
                <InstallButton variant="footer" />
              </li>
              <li>
                <a className="text-[var(--color-ink)] hover:text-[var(--color-ink-hi)]" href="/imprint">
                  {t("landing.footer.imprint")}
                </a>
              </li>
              <li>
                <a className="text-[var(--color-ink)] hover:text-[var(--color-ink-hi)]" href="/privacy">
                  {t("landing.footer.privacy")}
                </a>
              </li>
              <li>
                <a className="text-[var(--color-ink)] hover:text-[var(--color-ink-hi)]" href="/terms">
                  {t("landing.footer.terms")}
                </a>
              </li>
              <li>
                <a className="text-[var(--color-ink)] hover:text-[var(--color-ink-hi)]" href="mailto:mail@norbert-madarasz.de">
                  {t("landing.footer.contact")}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}
